<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  task: { type: Object, required: true },
  agents: { type: Array, default: () => [] },
});

const emit = defineEmits(['updated']);

const store = useStore();
const { t } = useI18n();
const showPicker = ref(false);

const members = computed(() => props.task.members || []);
const available = computed(() =>
  props.agents.filter(
    agent => !(props.task.member_ids || []).includes(agent.id)
  )
);

const update = async (action, payload) => {
  try {
    emit('updated', await store.dispatch(action, payload));
  } catch (error) {
    useAlert(t('TASKS.CARD.MEMBER_ERROR'));
  }
};

const add = agent => {
  showPicker.value = false;
  return update('tasks/addMember', { id: props.task.id, userId: agent.id });
};

const remove = member =>
  update('tasks/removeMember', { id: props.task.id, userId: member.id });
</script>

<template>
  <div class="flex flex-col gap-1.5 mb-6">
    <span
      class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
    >
      {{ t('TASKS.CARD.MEMBERS') }}
    </span>
    <div class="relative flex flex-wrap items-center gap-1.5">
      <button
        v-for="member in members"
        :key="member.id"
        class="rounded-full hover:opacity-70"
        :title="t('TASKS.CARD.REMOVE_MEMBER', { name: member.name })"
        @click="remove(member)"
      >
        <Avatar
          :name="member.name"
          :src="member.avatar_url"
          :size="28"
          rounded-full
        />
      </button>
      <button
        class="flex items-center justify-center border border-dashed rounded-full size-7 border-n-slate-7 text-n-slate-11 hover:bg-n-alpha-2"
        :title="t('TASKS.CARD.ADD_MEMBER')"
        @click="showPicker = !showPicker"
      >
        <span class="i-lucide-plus size-4" />
      </button>
      <div
        v-if="showPicker"
        class="absolute left-0 z-10 w-56 p-1 mt-1 overflow-y-auto border rounded-lg shadow-lg top-full max-h-60 bg-n-solid-2 border-n-weak"
      >
        <p v-if="!available.length" class="px-2 py-1.5 text-sm text-n-slate-10">
          {{ t('TASKS.CARD.NO_AGENTS_LEFT') }}
        </p>
        <button
          v-for="agent in available"
          :key="agent.id"
          class="flex items-center w-full gap-2 px-2 py-1.5 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-2"
          @click="add(agent)"
        >
          <Avatar
            :name="agent.name"
            :src="agent.thumbnail"
            :size="20"
            rounded-full
          />
          <span class="truncate">{{ agent.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
